import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  RefreshCw, Play, Trash2, Calendar, Database, Settings, 
  List, Clock, CheckCircle, XCircle, AlertCircle, Loader2
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Generate observation dates (May 15 & Nov 15 for each year, going back 8 years)
const generateObservationDates = () => {
  const dates = [];
  const currentYear = new Date().getFullYear();
  
  for (let year = currentYear; year >= currentYear - 8; year--) {
    dates.push(`${year}-11-15`);
    dates.push(`${year}-05-15`);
  }
  
  return dates;
};

const AVAILABLE_DATES = generateObservationDates();

// Mini curve chart
const MiniCurveChart = ({ curve, color = "#3b82f6", height = 40, width = 100 }) => {
  if (!curve || curve.length === 0) return null;
  
  const padding = 4;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;
  
  const values = curve.filter(v => v != null);
  if (values.length === 0) return null;
  
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;
  
  const points = values.map((val, idx) => {
    const x = padding + (idx / (values.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((val - minVal) / range) * chartHeight;
    return `${x},${y}`;
  }).join(" ");
  
  return (
    <svg width={width} height={height}>
      <polyline 
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default function CurvesPipelinePage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [state, setState] = useState({ status: "idle" });
  const [config, setConfig] = useState({});
  const [processedDates, setProcessedDates] = useState([]);
  const [dateStats, setDateStats] = useState({});
  const [runs, setRuns] = useState([]);
  const [logs, setLogs] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [curvesData, setCurvesData] = useState([]);
  const [activeTab, setActiveTab] = useState("obs-dates");
  
  // Date selection for running pipeline
  const [selectedDates, setSelectedDates] = useState([]);
  
  // Data filters
  const [filterDate, setFilterDate] = useState("");
  
  // Diagnostic state
  const [diagDate, setDiagDate] = useState("");
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState(null);
  
  // Fetch diagnostic data for a date
  const fetchDiagnostic = async (date) => {
    if (!date) return;
    setDiagLoading(true);
    setDiagData(null);
    try {
      const response = await fetch(`${API}/curves-pipeline/diagnose/${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDiagData(data);
      } else {
        toast.error("Failed to fetch diagnostic data");
      }
    } catch (error) {
      console.error("Failed to fetch diagnostic:", error);
      toast.error("Error fetching diagnostic");
    } finally {
      setDiagLoading(false);
    }
  };
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/curves-pipeline/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        setState(data.state || { status: "idle" });
        setConfig(data.config || {});
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, [token]);
  
  // Fetch processed dates
  const fetchProcessedDates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/curves-pipeline/dates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProcessedDates(data.dates || []);
        
        // Fetch stats for each processed date
        const statsMap = {};
        for (const date of (data.dates || []).slice(0, 20)) {
          try {
            const statsResp = await fetch(`${API}/curves-pipeline/dates/${date}/stats`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (statsResp.ok) {
              statsMap[date] = await statsResp.json();
            }
          } catch (e) {
            console.error(`Failed to fetch stats for ${date}:`, e);
          }
        }
        setDateStats(statsMap);
      }
    } catch (error) {
      console.error("Failed to fetch dates:", error);
    }
  }, [token]);
  
  // Fetch run history
  const fetchRuns = useCallback(async () => {
    try {
      const response = await fetch(`${API}/curves-pipeline/runs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRuns(data.runs || []);
      }
    } catch (error) {
      console.error("Failed to fetch runs:", error);
    }
  }, [token]);
  
  // Fetch logs for a run
  const fetchLogs = useCallback(async (runId) => {
    try {
      const response = await fetch(`${API}/curves-pipeline/runs/${runId}/logs?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setSelectedRunId(runId);
      }
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    }
  }, [token]);
  
  // Fetch curves data
  const fetchCurvesData = useCallback(async (obsDate) => {
    try {
      let url = `${API}/curves-pipeline/data?limit=100`;
      if (obsDate) url += `&observation_date=${obsDate}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCurvesData(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch curves data:", error);
    }
  }, [token]);
  
  // Load all data
  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchStats(),
      fetchProcessedDates(),
      fetchRuns(),
    ]);
    setLoading(false);
  }, [fetchStats, fetchProcessedDates, fetchRuns]);
  
  useEffect(() => {
    loadAll();
    
    // Poll for state updates when running
    const interval = setInterval(() => {
      fetchStats();
      if (state.status === "running") {
        fetchRuns();
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [loadAll, fetchStats, fetchRuns, state.status]);
  
  // Toggle date selection
  const toggleDate = (date) => {
    if (selectedDates.includes(date)) {
      setSelectedDates(selectedDates.filter(d => d !== date));
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };
  
  // Run pipeline for selected dates
  const handleRunPipeline = async () => {
    if (selectedDates.length === 0) {
      toast.error("Please select at least one observation date");
      return;
    }
    
    try {
      const response = await fetch(`${API}/curves-pipeline/run`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          observation_dates: selectedDates,
          delete_existing: true,
        }),
      });
      
      if (response.ok) {
        toast.success(`Pipeline started for ${selectedDates.length} dates`);
        setSelectedDates([]);
        fetchStats();
        fetchRuns();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start pipeline");
      }
    } catch (error) {
      toast.error("Failed to start pipeline");
    }
  };
  
  // Reprocess a single date
  const handleReprocess = async (date) => {
    try {
      const response = await fetch(`${API}/curves-pipeline/run`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          observation_dates: [date],
          delete_existing: true,
        }),
      });
      
      if (response.ok) {
        toast.success(`Reprocessing ${date}`);
        fetchStats();
        fetchRuns();
      } else {
        toast.error("Failed to reprocess");
      }
    } catch (error) {
      toast.error("Failed to reprocess");
    }
  };
  
  // Delete a specific observation date
  const handleDeleteDate = async (date) => {
    if (!window.confirm(`Delete all curves for ${date}?`)) return;
    
    try {
      const response = await fetch(`${API}/curves-pipeline/dates/${date}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Deleted ${result.deleted} curves for ${date}`);
        fetchProcessedDates();
        fetchStats();
      } else {
        toast.error("Failed to delete date");
      }
    } catch (error) {
      toast.error("Failed to delete date");
    }
  };
  
  // Cancel pipeline
  const handleCancel = async () => {
    try {
      await fetch(`${API}/curves-pipeline/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Cancel signal sent");
      fetchStats();
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };
  
  // Clear all pipeline data
  const handleClearAll = async () => {
    if (!window.confirm("Clear ALL pipeline data? This will delete all runs, logs, and curves.")) return;
    
    try {
      const response = await fetch(`${API}/curves-pipeline/clear-all`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Cleared: ${result.deleted.runs} runs, ${result.deleted.logs} logs, ${result.deleted.curves} curves`);
        loadAll();
      } else {
        toast.error("Failed to clear data");
      }
    } catch (error) {
      toast.error("Failed to clear data");
    }
  };
  
  // Check if date is processed
  const isProcessed = (date) => processedDates.includes(date);
  
  return (
    <div className="space-y-6" data-testid="curves-pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fund Performance Curves</h1>
          <p className="text-muted-foreground">
            Generate timing signals from fund holding patterns
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(state.status === "running" || state.status === "cancelling") ? (
            <Button variant="destructive" onClick={handleCancel} size="sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {state.status === "cancelling" ? "Cancelling..." : "Cancel"}
            </Button>
          ) : (
            <Badge variant="outline" className="capitalize">{state.status || "idle"}</Badge>
          )}
          <Button variant="outline" onClick={loadAll} disabled={loading} size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="ghost" onClick={handleClearAll} size="sm" className="text-destructive hover:text-destructive">
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Total Curves</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_curves?.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Unique Funds</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.unique_funds?.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Observation Dates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.unique_dates}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold capitalize">{state.status}</div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="obs-dates">Obs Dates</TabsTrigger>
          <TabsTrigger value="diagnostic">Diagnostic</TabsTrigger>
          <TabsTrigger value="data">Browse Data</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="run-history">Run History</TabsTrigger>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
        </TabsList>
        
        {/* Observation Dates Tab */}
        <TabsContent value="obs-dates" className="space-y-4">
          {/* Run Specific Dates */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                <CardTitle className="text-lg">Run Specific Observation Dates</CardTitle>
              </div>
              <CardDescription>
                Select one or more dates to process individually
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Pills */}
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_DATES.map(date => {
                  const processed = isProcessed(date);
                  const selected = selectedDates.includes(date);
                  
                  return (
                    <button
                      key={date}
                      onClick={() => toggleDate(date)}
                      className={`
                        px-3 py-1.5 rounded-full text-sm font-mono transition-all
                        flex items-center gap-1.5
                        ${selected 
                          ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background' 
                          : processed
                            ? 'bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80 border border-transparent'
                        }
                      `}
                    >
                      {date}
                      {processed && !selected && (
                        <CheckCircle className="w-3 h-3" />
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Action Bar */}
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm text-muted-foreground">
                  Select dates to process (most recent shown first)
                </span>
                <Button 
                  onClick={handleRunPipeline}
                  disabled={selectedDates.length === 0 || state.status === "running"}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Run Selected ({selectedDates.length})
                </Button>
              </div>
              
              {/* Progress indicator */}
              {state.status === "running" && state.progress && (
                <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm font-medium">{state.progress.message}</span>
                  </div>
                  {state.progress.current_date && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Processing: {state.progress.current_date}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Processed Dates Table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Processed Observation Dates</CardTitle>
                  <CardDescription>
                    Track, delete, or reprocess specific observation dates
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchProcessedDates}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {processedDates.length} observation dates tracked. Curves computed semi-annually (May 15 & Nov 15).
              </p>
              
              {processedDates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No observation dates processed yet. Select dates above to run the pipeline.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left p-3 font-medium">Observation Date</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-right p-3 font-medium">Curves</th>
                        <th className="text-right p-3 font-medium">Funds</th>
                        <th className="text-right p-3 font-medium">Sectors</th>
                        <th className="text-left p-3 font-medium">Last Processed</th>
                        <th className="text-right p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedDates.map(date => {
                        const ds = dateStats[date] || {};
                        return (
                          <tr key={date} className="border-b hover:bg-muted/30">
                            <td className="p-3 font-mono">{date}</td>
                            <td className="p-3">
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                completed
                              </Badge>
                            </td>
                            <td className="text-right p-3 font-mono">
                              {ds.total_curves?.toLocaleString() || "-"}
                            </td>
                            <td className="text-right p-3 font-mono">
                              {ds.unique_funds?.toLocaleString() || "-"}
                            </td>
                            <td className="text-right p-3 font-mono">
                              {Object.keys(ds.sectors || {}).length || "-"}
                            </td>
                            <td className="p-3 text-muted-foreground text-xs">
                              -
                            </td>
                            <td className="text-right p-3">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReprocess(date)}
                                  disabled={state.status === "running"}
                                  className="h-8 w-8 p-0"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteDate(date)}
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Diagnostic Tab */}
        <TabsContent value="diagnostic" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <CardTitle className="text-lg">Data Diagnostic</CardTitle>
              </div>
              <CardDescription>
                Analyze data availability for an observation date BEFORE running the pipeline
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Selection */}
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="text-sm text-muted-foreground">Select Observation Date</label>
                  <select
                    value={diagDate}
                    onChange={(e) => setDiagDate(e.target.value)}
                    className="w-full mt-1 rounded-md border bg-background px-3 py-2"
                  >
                    <option value="">Select a date...</option>
                    {AVAILABLE_DATES.map(date => (
                      <option key={date} value={date}>{date}</option>
                    ))}
                  </select>
                </div>
                <Button 
                  onClick={() => fetchDiagnostic(diagDate)}
                  disabled={!diagDate || diagLoading}
                >
                  {diagLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Database className="w-4 h-4 mr-2" />
                  )}
                  Analyze
                </Button>
              </div>
              
              {/* Diagnostic Results */}
              {diagLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-2">Analyzing data...</span>
                </div>
              )}
              
              {diagData && !diagLoading && (
                <div className="space-y-6 pt-4">
                  {/* Config Info */}
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">Date Windows</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Lookback Start:</span>
                        <span className="ml-2 font-mono">{diagData.config?.lookback_start}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Effective Date:</span>
                        <span className="ml-2 font-mono">{diagData.config?.effective_date}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Alpha End:</span>
                        <span className="ml-2 font-mono">{diagData.config?.alpha_end_date}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    <Card className="p-4">
                      <div className="text-sm text-muted-foreground">Buy Events</div>
                      <div className="text-2xl font-bold">{diagData.holdings_source?.total_buy_events?.toLocaleString() || diagData.sectors?.total_buy_events?.toLocaleString() || 0}</div>
                    </Card>
                    <Card className="p-4">
                      <div className="text-sm text-muted-foreground">Unique Funds</div>
                      <div className="text-2xl font-bold">{diagData.holdings_source?.unique_funds?.toLocaleString() || 0}</div>
                    </Card>
                    <Card className="p-4">
                      <div className="text-sm text-muted-foreground">Stock Symbols</div>
                      <div className="text-2xl font-bold text-green-500">{diagData.holdings_source?.stock_symbols?.toLocaleString() || 0}</div>
                    </Card>
                    <Card className="p-4">
                      <div className="text-sm text-muted-foreground">ETF/Fund Symbols</div>
                      <div className="text-2xl font-bold text-amber-500">{diagData.holdings_source?.etf_fund_symbols?.toLocaleString() || 0}</div>
                    </Card>
                    <Card className="p-4">
                      <div className="text-sm text-muted-foreground">Stock Alpha Coverage</div>
                      <div className={`text-2xl font-bold ${diagData.alpha_pipeline?.final_coverage_pct < 50 ? 'text-red-500' : 'text-green-500'}`}>
                        {diagData.alpha_pipeline?.final_coverage_pct || 0}%
                      </div>
                    </Card>
                  </div>
                  
                  {/* Sector Breakdown */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3">Sector Analysis (from Buy Events)</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Events with sector:</span>
                        <span className="ml-2 font-mono text-green-500">{diagData.sectors?.events_with_sector?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Events WITHOUT sector:</span>
                        <span className="ml-2 font-mono text-red-500">{diagData.sectors?.events_without_sector?.toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <h5 className="text-sm font-medium text-muted-foreground mb-2">Sectors Found ({diagData.sectors?.unique_sectors || 0})</h5>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                      {diagData.sectors?.sectors_found?.map(sector => (
                        <div key={sector} className="flex justify-between p-2 bg-muted/30 rounded text-sm">
                          <span>{sector}</span>
                          <span className="font-mono text-muted-foreground">
                            {diagData.sectors?.symbols_per_sector?.[sector] || 0} sym / {diagData.sectors?.funds_per_sector?.[sector] || 0} funds
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Alpha Pipeline Coverage */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3">
                      Alpha Pipeline Coverage - Stocks Only 
                      <span className="text-sm font-normal text-muted-foreground ml-2">
                        (sampled {diagData.alpha_pipeline?.sampled_symbols?.toLocaleString()} of {diagData.alpha_pipeline?.total_stock_symbols?.toLocaleString()} stock symbols)
                      </span>
                    </h4>
                    <div className="grid grid-cols-5 gap-2 text-sm mb-4">
                      <div className="p-2 bg-muted/30 rounded text-center">
                        <div className="text-lg font-bold">{diagData.alpha_pipeline?.pipeline_coverage?.stocks_sampled?.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">Stocks Sampled</div>
                      </div>
                      <div className="p-2 bg-muted/30 rounded text-center">
                        <div className="text-lg font-bold">{diagData.alpha_pipeline?.pipeline_coverage?.in_stock_universe?.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">In Universe</div>
                      </div>
                      <div className="p-2 bg-muted/30 rounded text-center">
                        <div className="text-lg font-bold">{diagData.alpha_pipeline?.pipeline_coverage?.in_stock_perf_raw?.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">In Perf Raw</div>
                      </div>
                      <div className="p-2 bg-muted/30 rounded text-center">
                        <div className="text-lg font-bold">{diagData.alpha_pipeline?.pipeline_coverage?.in_stock_alpha?.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">In Alpha</div>
                      </div>
                      <div className="p-2 bg-green-500/20 rounded text-center">
                        <div className="text-lg font-bold text-green-500">{diagData.alpha_pipeline?.pipeline_coverage?.with_enough_alpha_days?.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">Enough Days</div>
                      </div>
                    </div>
                    
                    {/* ETF/Fund info */}
                    {diagData.alpha_pipeline?.total_etf_fund_symbols > 0 && (
                      <div className="mb-4 p-2 bg-amber-500/10 border border-amber-500/20 rounded text-sm">
                        <span className="text-amber-500 font-medium">{diagData.alpha_pipeline?.total_etf_fund_symbols?.toLocaleString()}</span>
                        <span className="text-muted-foreground ml-1">ETF/fund symbols excluded from alpha analysis (not processable)</span>
                      </div>
                    )}
                    
                    {/* Drop-off details */}
                    <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-3">
                      <h5 className="font-medium mb-2 text-amber-500 text-sm">Pipeline Drop-off (Stocks Only)</h5>
                      <div className="space-y-1 text-xs font-mono">
                        <div>Stocks → Universe: {diagData.alpha_pipeline?.drop_off?.stocks_to_universe}</div>
                        <div>Universe → Perf: {diagData.alpha_pipeline?.drop_off?.universe_to_perf}</div>
                        <div>Perf → Alpha: {diagData.alpha_pipeline?.drop_off?.perf_to_alpha}</div>
                        <div>Alpha → Enough Days (200+): {diagData.alpha_pipeline?.drop_off?.alpha_to_enough_days}</div>
                      </div>
                    </div>
                    
                    {/* Missing symbols */}
                    {diagData.alpha_pipeline?.sample_missing_from_universe?.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="text-xs text-muted-foreground mb-1">Sample symbols missing from Stock Universe:</div>
                        <div className="flex flex-wrap gap-1">
                          {diagData.alpha_pipeline?.sample_missing_from_universe?.slice(0, 15).map(sym => (
                            <span key={sym} className="px-2 py-0.5 bg-red-500/10 text-red-400 rounded text-xs font-mono">
                              {sym}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {diagData.alpha_pipeline?.sample_insufficient_alpha?.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="text-xs text-muted-foreground mb-1">Sample symbols with insufficient alpha days:</div>
                        <div className="flex flex-wrap gap-1">
                          {diagData.alpha_pipeline?.sample_insufficient_alpha?.slice(0, 15).map(sym => (
                            <span key={sym} className="px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded text-xs font-mono">
                              {sym}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Potential Curves Estimate */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3">Estimated Curves (with valid alpha)</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Sector</th>
                            <th className="text-right p-2">Funds</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(diagData.potential_curves?.funds_per_sector_with_valid_alpha || {}).map(([sector, count]) => (
                            <tr key={sector} className="border-b border-border/50">
                              <td className="p-2">{sector}</td>
                              <td className="text-right p-2 font-mono">{count?.toLocaleString()}</td>
                            </tr>
                          ))}
                          <tr className="font-medium bg-green-500/10">
                            <td className="p-2">Total Estimated Curves</td>
                            <td className="text-right p-2 font-mono text-green-500">
                              {diagData.potential_curves?.estimated_total_curves?.toLocaleString() || 0}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {diagData.potential_curves?.note || "Each fund gets 1 curve per sector"}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Browse Data Tab */}
        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Browse Curves Data</CardTitle>
              <CardDescription>View generated fund curves by observation date</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Filter */}
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="text-sm text-muted-foreground">Observation Date</label>
                  <select
                    value={filterDate}
                    onChange={(e) => {
                      setFilterDate(e.target.value);
                      if (e.target.value) fetchCurvesData(e.target.value);
                    }}
                    className="w-full mt-1 rounded-md border bg-background px-3 py-2"
                  >
                    <option value="">Select a date...</option>
                    {processedDates.map(date => (
                      <option key={date} value={date}>{date}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Results Table */}
              {curvesData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">Fund</th>
                        <th className="text-left p-2 font-medium">Sector</th>
                        <th className="text-left p-2 font-medium">Cluster</th>
                        <th className="text-left p-2 font-medium">Raw Curve</th>
                        <th className="text-left p-2 font-medium">Cluster Curve</th>
                        <th className="text-right p-2 font-medium">Buys</th>
                      </tr>
                    </thead>
                    <tbody>
                      {curvesData.slice(0, 50).map((curve, idx) => {
                        const rawCurve = [curve.m1, curve.m2, curve.m3, curve.m4, curve.m5, curve.m6,
                                          curve.m7, curve.m8, curve.m9, curve.m10, curve.m11, curve.m12];
                        const clustCurve = [curve.clust_m1, curve.clust_m2, curve.clust_m3, curve.clust_m4,
                                            curve.clust_m5, curve.clust_m6, curve.clust_m7, curve.clust_m8,
                                            curve.clust_m9, curve.clust_m10, curve.clust_m11, curve.clust_m12];
                        return (
                          <tr key={idx} className="border-b hover:bg-muted/50">
                            <td className="p-2 font-mono text-xs">{curve.fund}</td>
                            <td className="p-2 text-xs">{curve.sector_code}</td>
                            <td className="p-2">
                              <Badge variant="outline" className="text-xs">{curve.cluster_id}</Badge>
                            </td>
                            <td className="p-2">
                              <MiniCurveChart curve={rawCurve} color="#22c55e" />
                            </td>
                            <td className="p-2">
                              <MiniCurveChart curve={clustCurve} color="#3b82f6" />
                            </td>
                            <td className="text-right p-2">{curve.n_buys}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {filterDate ? "No curves found for this date" : "Select an observation date to view curves"}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pipeline Logs</CardTitle>
              <CardDescription>View logs from recent pipeline runs</CardDescription>
            </CardHeader>
            <CardContent>
              {runs.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {/* Run selector */}
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-muted-foreground">Select Run</div>
                    {runs.slice(0, 10).map(run => (
                      <div
                        key={run.run_id}
                        onClick={() => fetchLogs(run.run_id)}
                        className={`p-2 rounded cursor-pointer transition-colors ${
                          selectedRunId === run.run_id 
                            ? 'bg-primary/10 border border-primary' 
                            : 'bg-muted/30 hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs">{run.run_id}</span>
                          <Badge variant={run.status === "completed" ? "default" : "secondary"} className="text-xs">
                            {run.status}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {run.started_at?.substring(0, 16).replace('T', ' ')}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Log viewer */}
                  <div className="col-span-2">
                    <div className="text-sm font-medium text-muted-foreground mb-2">
                      Logs {selectedRunId && `(${selectedRunId})`}
                    </div>
                    <div className="bg-muted/30 rounded-lg p-3 max-h-96 overflow-y-auto font-mono text-xs space-y-1">
                      {logs.length > 0 ? (
                        logs.map((log, idx) => (
                          <div
                            key={idx}
                            className={`p-1 rounded ${
                              log.level === 'error' ? 'bg-red-500/10 text-red-400' :
                              log.level === 'warning' ? 'bg-amber-500/10 text-amber-400' :
                              'text-muted-foreground'
                            }`}
                          >
                            <span className="opacity-50">{log.timestamp?.substring(11, 19)}</span>
                            {' '}{log.message}
                          </div>
                        ))
                      ) : (
                        <div className="text-muted-foreground text-center py-8">
                          Select a run to view logs
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No pipeline runs yet
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Run History Tab */}
        <TabsContent value="run-history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Run History</CardTitle>
              <CardDescription>History of all pipeline executions</CardDescription>
            </CardHeader>
            <CardContent>
              {runs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No runs yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium">Run ID</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Started</th>
                        <th className="text-left p-3 font-medium">Duration</th>
                        <th className="text-right p-3 font-medium">Dates</th>
                        <th className="text-right p-3 font-medium">Curves</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map(run => (
                        <tr key={run.run_id} className="border-b hover:bg-muted/30">
                          <td className="p-3 font-mono text-xs">{run.run_id}</td>
                          <td className="p-3">
                            <Badge variant={
                              run.status === "completed" ? "default" :
                              run.status === "failed" ? "destructive" :
                              run.status === "running" ? "secondary" : "outline"
                            }>
                              {run.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-muted-foreground">
                            {run.started_at?.substring(0, 19).replace('T', ' ')}
                          </td>
                          <td className="p-3 text-muted-foreground">
                            {run.duration_seconds ? `${run.duration_seconds.toFixed(1)}s` : '-'}
                          </td>
                          <td className="text-right p-3">{run.dates_processed || '-'}</td>
                          <td className="text-right p-3 font-mono">
                            {run.curves_written?.toLocaleString() || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Configuration Tab */}
        <TabsContent value="configuration" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pipeline Configuration</CardTitle>
              <CardDescription>Current settings for curve generation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground">Data Parameters</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span>Lookback Period</span>
                      <span className="font-mono">{config.lookback_months} months</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span>Holding Days</span>
                      <span className="font-mono">{config.holding_days} days</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span>Window Size</span>
                      <span className="font-mono">{config.window_size} days</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span>Profile Length</span>
                      <span className="font-mono">{config.profile_length} months</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground">Processing Settings</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span>Sector Emphasis Weight</span>
                      <span className="font-mono">{config.sector_emphasis_weight}x</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span>Model Version</span>
                      <span className="font-mono">{config.model_version}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span>Use Modal</span>
                      <span className="font-mono">{config.use_modal ? "Yes" : "No"}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Modal Parallelization Settings */}
              <div className="mt-6 pt-4 border-t">
                <h4 className="font-medium text-sm text-muted-foreground mb-4">Modal Parallelization</h4>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex justify-between py-2 border-b">
                    <span>Funds per Batch</span>
                    <span className="font-mono">{config.max_funds_per_batch || 200}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span>Max Concurrent Jobs</span>
                    <span className="font-mono">{config.max_concurrent_modal_jobs || 50}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Jobs run in parallel waves. Increase concurrent jobs for faster processing (costs more Modal compute).
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
